import StaffProfiles from '../../components/StaffProfiles';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { checkPermission } from '../../utils/permissionUtils';
import { useToast } from '../../context/ToastContext';
import { sortEventsByDate, formatShortDate } from '../../utils/eventUtils';
import '../../App.css';
import './EventsListPage.css';
import DataTable, { Column } from '../../components/DataTable';
import FilterBar, { ForeignKeyOption } from '../../components/FilterBar';
import { eventFields, FilterValue, applyFilters } from '../../utils/entityFields';
import { ConfirmModal, SkeletonTable } from '../../components/ui';
import EmptyState from '../../components/ui/EmptyState';
import PageHeader from '../../components/PageHeader';
import EventStatusBadge, { type EventStatus } from '../../components/EventStatusBadge';
import EventStatusActions from '../../components/EventStatusActions';
import { translateError } from '../../utils/errorMessages';

interface IResponsibleUser {
  aboutMe?: string;
  photoUrl?: string | null;
  _id: string;
  name: string;
}

interface IEvent {
  _id: string;
  name: string;
  description: string;
  startTime: string;
  endTime: string;
  defaultAttendance: boolean | null;
  responsible?: IResponsibleUser;
  template?: { _id: string; name: string } | null;
  activityId?: number | null;
  courseId?: number | null;
  status?: EventStatus;
}

interface INamed {
  _id: string | number;
  name: string;
}

const EventsListPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const [events, setEvents] = useState<IEvent[]>([]);
  const [responsibleUsers, setResponsibleUsers] = useState<IResponsibleUser[]>([]);
  const [activities, setActivities] = useState<INamed[]>([]);
  const [courses, setCourses] = useState<INamed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterValue[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [expandedTemplates, setExpandedTemplates] = useState<Set<string>>(new Set());

  // Permission checks
  const canCreate = hasPermission('events:write');
  const canEdit = hasPermission('events:write');
  const canDelete = hasPermission('events:delete');
  const canViewUsers = hasPermission('users:read');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch events
      const eventsRes = await api.events.list();
      setEvents(eventsRes.data as IEvent[]);

      // Activity/course options power the filter dropdowns (non-critical).
      try {
        const [activitiesRes, coursesRes] = await Promise.all([
          api.activities.list(),
          api.courses.list(),
        ]);
        setActivities((activitiesRes.data as INamed[]).map(a => ({ _id: String(a._id), name: a.name })));
        setCourses((coursesRes.data as INamed[]).map(c => ({ _id: String(c._id), name: c.name })));
      } catch {
        // Ignore — filters degrade gracefully without options.
      }

      // Fetch users if user has permission (for responsible filter — admins/owners only)
      if (canViewUsers) {
        try {
          const usersRes = await api.users.list();
          const usersData = usersRes.data as { _id: string; name: string; role?: { name: string; permissions?: Record<string, number> }; isSuperAdmin?: boolean }[];
          setResponsibleUsers(
            usersData
              .filter(u =>
                u.isSuperAdmin ||
                u.role?.name === 'Admin' ||
                u.role?.name === 'Owner' ||
                checkPermission(u.role?.permissions, 'events:write')
              )
              .map(u => ({ _id: u._id, name: u.name }))
          );
        } catch {
          // Ignore error - user might not have permission
        }
      }
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, [canViewUsers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteClick = (event: IEvent) => {
    if (!canDelete) return;
    setDeleteId(event._id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await api.events.delete(deleteId);
      fetchData();
      addToast(t('events.deleteSuccess'), 'success');
    } catch (err: any) {
      addToast(translateError(err, t, 'events.deleteError'), 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  };

  // Create foreign key options for the filter
  const foreignKeyOptions = useMemo<Record<string, ForeignKeyOption[]>>(() => ({
    admins: responsibleUsers.map(u => ({ value: u._id, label: u.name })),
    activities: activities.map(a => ({ value: String(a._id), label: a.name })),
    courses: courses.map(c => ({ value: String(c._id), label: c.name })),
  }), [responsibleUsers, activities, courses]);

  // Derive unique templates from events for quick filter chips
  const templateChips = useMemo(() => {
    const map = new Map<string, { _id: string; name: string; count: number }>();
    let standaloneCount = 0;
    for (const event of events) {
      if (event.template) {
        const existing = map.get(event.template._id);
        if (existing) {
          existing.count++;
        } else {
          map.set(event.template._id, { _id: event.template._id, name: event.template.name, count: 1 });
        }
      } else {
        standaloneCount++;
      }
    }
    return { templates: Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name)), standaloneCount };
  }, [events]);

  const columns: Column<IEvent>[] = useMemo(() => [
    {
      header: t('common.name'),
      key: 'name',
      render: event => <><span>{event.name}</span><StaffProfiles people={event.responsible ? [event.responsible] : []} /></>,
    },
    {
      header: t('common.description'),
      key: 'description',
    },
    {
      header: t('events.startTime'),
      sortValue: (event) => (event.startTime ? new Date(event.startTime).getTime() : null),
      render: (event) => new Date(event.startTime).toLocaleString(),
    },
    {
      header: t('events.endTime'),
      sortValue: (event) => (event.endTime ? new Date(event.endTime).getTime() : null),
      render: (event) => new Date(event.endTime).toLocaleString(),
    },
    {
      header: t('events.defaultAttendance'),
      render: (event) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: event.defaultAttendance === true ? 'rgba(40, 167, 69, 0.2)' : event.defaultAttendance === false ? 'rgba(220, 53, 69, 0.2)' : 'rgba(128, 128, 128, 0.2)',
          color: event.defaultAttendance === true ? '#28a745' : event.defaultAttendance === false ? '#dc3545' : '#808080',
          fontSize: '0.85rem',
        }}>
          {event.defaultAttendance === true ? t('common.yes') : event.defaultAttendance === false ? t('common.no') : t('common.maybe')}
        </span>
      ),
    },
    {
      header: t('eventStatus.label'),
      sortValue: (event) => event.status ?? 'scheduled',
      render: (event) => <EventStatusBadge status={event.status} />,
    },
  ], [t]);

  // Rendered inside the table's own actions column, beside edit and delete, so
  // the row reads as one set of controls rather than two scattered groups.
  const renderStatusActions = useCallback((event: IEvent) => (
    <EventStatusActions
      eventId={event._id}
      eventName={event.name}
      status={event.status}
      onChanged={fetchData}
    />
  ), [fetchData]);

  // Apply template chip filter + user filters, then sort
  const filteredEvents = useMemo(() => {
    let result = events;
    if (selectedTemplateId === 'standalone') {
      result = events.filter(e => !e.template);
    } else if (selectedTemplateId) {
      result = events.filter(e => e.template?._id === selectedTemplateId);
    }
    result = applyFilters(result, filters);
    return sortEventsByDate(result);
  }, [events, filters, selectedTemplateId]);

  // Whether we show grouped view: "All" chip selected + no active user filters
  const showGrouped = !selectedTemplateId && filters.length === 0;

  // Build grouped data for the "All" view
  const groupedData = useMemo(() => {
    if (!showGrouped) return null;

    const templateGroups = new Map<string, { templateName: string; events: IEvent[] }>();
    const standalone: IEvent[] = [];

    for (const event of filteredEvents) {
      if (event.template) {
        const group = templateGroups.get(event.template._id);
        if (group) {
          group.events.push(event);
        } else {
          templateGroups.set(event.template._id, {
            templateName: event.template.name,
            events: [event],
          });
        }
      } else {
        standalone.push(event);
      }
    }

    return { templateGroups, standalone };
  }, [filteredEvents, showGrouped]);

  const toggleTemplateGroup = (templateId: string) => {
    setExpandedTemplates(prev => {
      const next = new Set(prev);
      if (next.has(templateId)) {
        next.delete(templateId);
      } else {
        next.add(templateId);
      }
      return next;
    });
  };

  const handleFilterChange = useCallback((newFilters: FilterValue[]) => {
    setFilters(newFilters);
  }, []);

  // Find the next upcoming event in a group
  const getNextUpcoming = (groupEvents: IEvent[]): string | null => {
    const now = new Date().getTime();
    for (const ev of groupEvents) {
      if (new Date(ev.startTime).getTime() >= now) {
        return formatShortDate(ev.startTime, i18n.language);
      }
    }
    return null;
  };

  // Render the grouped table manually
  const renderGroupedTable = () => {
    if (!groupedData) return null;
    const { templateGroups, standalone } = groupedData;

    // canEdit also gates the status actions, so an admin with events:write but
    // no events:delete still gets the column — with edit + status, no delete.
    const hasActions = canEdit || canDelete;
    const colCount = columns.length + (hasActions ? 1 : 0);

    // Build ordered list: template groups sorted by their next upcoming event, then standalone
    const sortedGroups = Array.from(templateGroups.entries()).sort((a, b) => {
      // Sort by first event's startTime (already sorted within group)
      const aTime = new Date(a[1].events[0].startTime).getTime();
      const bTime = new Date(b[1].events[0].startTime).getTime();
      return aTime - bTime;
    });

    return (
      <div className="table-container">
        <table className="data-table" role="table">
          <thead>
            <tr role="row">
              {columns.map((col, index) => (
                <th key={index} role="columnheader" scope="col">{col.header}</th>
              ))}
              {hasActions && <th role="columnheader" scope="col">{t('common.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {sortedGroups.map(([templateId, group]) => {
              const isExpanded = expandedTemplates.has(templateId);
              const nextDate = getNextUpcoming(group.events);
              return (
                <React.Fragment key={`group-${templateId}`}>
                  <tr
                    className="event-group-header"
                    onClick={() => toggleTemplateGroup(templateId)}
                    role="row"
                    aria-expanded={isExpanded}
                    aria-label={isExpanded
                      ? t('events.collapseGroup', { name: group.templateName })
                      : t('events.expandGroup', { name: group.templateName })
                    }
                  >
                    <td colSpan={colCount}>
                      <div className="event-group-header__content">
                        <span className={`event-group-header__chevron ${isExpanded ? 'event-group-header__chevron--expanded' : 'event-group-header__chevron--collapsed'}`}>
                          ▼
                        </span>
                        <span className="event-group-header__name">{group.templateName}</span>
                        <span className="event-group-header__meta">
                          <span className="event-group-header__count">
                            {t('events.templateGroup', { count: group.events.length })}
                          </span>
                          {nextDate && (
                            <span className="event-group-header__next">
                              {t('events.nextEvent', { date: nextDate })}
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && group.events.map(event => (
                    <tr key={event._id} role="row" className="event-group-child">
                      {columns.map((col, index) => (
                        <td key={index} role="cell">
                          {col.render ? col.render(event) : col.key ? (event as any)[col.key] : null}
                        </td>
                      ))}
                      {hasActions && (
                        <td role="cell">
                          <div className="grid-actions">
                            {canEdit && (
                              <Link
                                to={`/admin/events/${event._id}/edit`}
                                className="btn btn--primary btn--sm"
                                aria-label={`${t('common.edit')} ${event.name}`}
                                title={t('common.edit')}
                              >
                                <i className="fas fa-edit" aria-hidden="true"></i>
                              </Link>
                            )}
                            {renderStatusActions(event)}
                            {canDelete && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteClick(event); }}
                                className="btn btn--danger btn--sm"
                                type="button"
                                aria-label={`${t('common.delete')} ${event.name}`}
                                title={t('common.delete')}
                              >
                                <i className="fas fa-trash" aria-hidden="true"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            {standalone.map(event => (
              <tr key={event._id} role="row">
                {columns.map((col, index) => (
                  <td key={index} role="cell">
                    {col.render ? col.render(event) : col.key ? (event as any)[col.key] : null}
                  </td>
                ))}
                {hasActions && (
                  <td role="cell">
                    <div className="grid-actions">
                      {canEdit && (
                        <Link
                          to={`/admin/events/${event._id}/edit`}
                          className="btn btn--primary btn--sm"
                          aria-label={`${t('common.edit')} ${event.name}`}
                          title={t('common.edit')}
                        >
                          <i className="fas fa-edit" aria-hidden="true"></i>
                        </Link>
                      )}
                      {renderStatusActions(event)}
                      {canDelete && (
                        <button
                          onClick={() => handleDeleteClick(event)}
                          className="btn btn--danger btn--sm"
                          type="button"
                          aria-label={`${t('common.delete')} ${event.name}`}
                          title={t('common.delete')}
                        >
                          <i className="fas fa-trash" aria-hidden="true"></i>
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  return (
    <div className="admin-list-page">
      <PageHeader
        title={t('events.title')}
        helpKey="help.admin.events"
        action={canCreate ? <Link to="/admin/events/create" className="btn btn--primary btn--md">{t('events.createNew')}</Link> : undefined}
      />

      {templateChips.templates.length > 0 && (
        <div className="quick-filters">
          <button
            type="button"
            className={`quick-filters__chip${selectedTemplateId === '' ? ' quick-filters__chip--active' : ''}`}
            onClick={() => setSelectedTemplateId('')}
          >
            {t('events.allTemplates')}<span className="quick-filters__count">({events.length})</span>
          </button>
          {templateChips.templates.map(tpl => (
            <button
              type="button"
              key={tpl._id}
              className={`quick-filters__chip${selectedTemplateId === tpl._id ? ' quick-filters__chip--active' : ''}`}
              onClick={() => setSelectedTemplateId(tpl._id)}
            >
              {tpl.name}<span className="quick-filters__count">({tpl.count})</span>
            </button>
          ))}
          {templateChips.standaloneCount > 0 && (
            <button
              type="button"
              className={`quick-filters__chip${selectedTemplateId === 'standalone' ? ' quick-filters__chip--active' : ''}`}
              onClick={() => setSelectedTemplateId('standalone')}
            >
              {t('events.standalone')}<span className="quick-filters__count">({templateChips.standaloneCount})</span>
            </button>
          )}
        </div>
      )}

      <FilterBar fields={eventFields} onFilterChange={handleFilterChange} foreignKeyOptions={foreignKeyOptions} />

      {loading ? (
        <SkeletonTable rows={5} columns={6} />
      ) : filteredEvents.length === 0 ? (
        <EmptyState
          title={t('events.emptyTitle')}
          description={t('events.emptyDescription')}
          action={canCreate ? { label: t('events.createNew'), href: '/admin/events/create' } : undefined}
        />
      ) : showGrouped ? (
        renderGroupedTable()
      ) : (
        <DataTable
          data={filteredEvents}
          columns={columns}
          editLink={canEdit ? (event) => `/admin/events/${event._id}/edit` : undefined}
          onDelete={canDelete ? handleDeleteClick : undefined}
          renderActions={canEdit ? renderStatusActions : undefined}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title={t('events.deleteTitle')}
        message={t('events.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default EventsListPage;
